import { Box, Typography } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import { Skeleton } from '@material-ui/lab'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useUser } from 'components/providers/UserProvider'
import { useStockStore } from 'lib/hooks/useStockStore'
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CloseButton } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Grid } from 'react-virtualized'
import { CardDataItem, Game, isSingle, LocalizedCard } from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import './autocompleteinput.css'
import { filterOptions } from './filterOptions'

interface ListboxComponentProps {
  children?: React.ReactNode
  onKeyboardSelect?: (itemIndex: number) => void
  inputRef?: React.RefObject<HTMLInputElement>
  columns: number
  columnWidth: number
  windowWidth: number
  windowHeight: number
  activeCell: number
  setListboxOpen: React.Dispatch<React.SetStateAction<boolean>>
  setActiveCell: React.Dispatch<React.SetStateAction<number>>
  setActiveValue: (index: number) => void
}

const ListboxComponent = forwardRef<HTMLDivElement, ListboxComponentProps>(
  function ListboxComponent(props, ref) {
    const {
      children,
      inputRef,
      onKeyboardSelect,
      activeCell,
      setActiveCell,
      setListboxOpen,
      columns,
      columnWidth,
      windowWidth,
      windowHeight,
      setActiveValue: updateActiveCellIndexAndValue,
      ...other
    } = props

    const { t } = useTranslation()
    const itemData = Children.toArray(children)
    const rowCount = Math.ceil(itemData.length / columns)
    const containerRef = useRef<HTMLDivElement>(null)
    const rowHeight = 340

    useEffect(() => {
      if (ref && containerRef.current) {
        // @ts-ignore
        ref.current = containerRef.current
      }
    }, [ref])

    const cellRenderer = ({ columnIndex, rowIndex, key, style }: any) => {
      const index = rowIndex * columns + columnIndex
      if (index >= itemData.length) return null

      const child = itemData[index]
      const isActive = index === activeCell

      return (
        <div
          key={key}
          style={style}
          onMouseEnter={() => {
            updateActiveCellIndexAndValue(index) // updated activeCell when mouse is over a cell
          }}
        >
          <div
            className="result-item-container"
            style={{
              margin: '8px',
              border: isActive ? '2px solid #013685' : 'none',
              background: isActive
                ? 'linear-gradient(140deg, rgba(24, 47, 117, 0.9) 0%, rgba(36, 140, 215, 0.9) 100%)'
                : 'transparent',
            }}
          >
            {isValidElement(child) ? cloneElement(child) : child}
          </div>
        </div>
      )
    }

    // Calculate consistent size based on number of results
    const totalWidth = columns * columnWidth + 32 // 32 is sum of padding left and right
    const maxHeight = windowHeight - 220

    return (
      <>
        <div
          className="grid-container-explanation d-flex justify-content-between align-items-center"
          style={{
            width: windowWidth <= 1280 ? totalWidth - 193 : totalWidth - 257,
            left: windowWidth <= 1280 ? '191px' : '255px',
          }}
        >
          <Typography variant="body2">{t('addArticles.helpToChoseCard')}</Typography>
          <CloseButton onClick={() => setListboxOpen(false)} />
        </div>
        <div
          {...other}
          ref={containerRef}
          className="grid-container"
          style={{
            width: totalWidth,
            height: itemData.length > columns ? maxHeight : rowHeight + 50,
            overflowY: 'auto',
            outline: 'none',
          }}
        >
          <Grid
            cellRenderer={cellRenderer}
            columnCount={columns}
            columnWidth={columnWidth}
            rowCount={rowCount}
            rowHeight={rowHeight}
            width={totalWidth}
            height={itemData.length > columns ? maxHeight - 25 : rowHeight + 10} // grid container must be smaller than parent container, includig header and padding
            scrollToRow={Math.floor(activeCell / columns)}
          />
        </div>
      </>
    )
  }
)

const cardLabel = (card: CardDataItem, rarities: Partial<Game['rarities']> = {}): any => {
  const rarityLabel = rarities[card.rarity]?.filterLabel || ''
  if (card.name && card.set && card.cn) {
    return (
      <span>
        {`${card.name}`} <br />
        {`#${card.cn} ${card.set} ${rarityLabel}`}
      </span>
    )
  } else if (card.name && card.set) {
    return (
      <span>
        {`${card.name}`} <br />
        {`${card.set} ${rarityLabel}`}
      </span>
    )
  } else if (card.name) {
    return `${card.name}`
  } else {
    return ''
  }
}

const normalizeSetCodeForKeyrune = (setCode: string): string => {
  const code = setCode.trim().toLowerCase()
  if ((code.length === 4 && code[0] === 'x') || code[0] === 'p') return code.slice(1)
  else return code
}

export interface AutocompleteInputProps {
  autocompleteRef?: any
  card?: CardDataItem | null
  expansionCode?: string
  handleCardChange: (newCard: CardDataItem) => void
  activeLanguage?: 'en' | 'fr' | 'es' | 'de' | 'it'
  rarities?: Game['rarities']
  useKeyruneExpansionIcons?: boolean
  cardData: LocalizedCard[]
  setShowOverlay?: React.Dispatch<React.SetStateAction<boolean>>
}

const getDebounceSpeed = (pauses: number[]) => {
  if (!pauses.length) return 2000
  else return (pauses.reduce((acc, pause) => (acc += pause), 0) / pauses.length) * 2
}

export default function AutocompleteInput(props: AutocompleteInputProps): JSX.Element {
  const { t } = useTranslation()
  const { activeGame } = useUser()

  const options = useMemo(() => {
    let filtered = props.cardData.filter(
      (card) => card.attributes[11] === activeGame.idGame || card.attributes[11] === 0
    )
    if (props.expansionCode) filtered = filtered.filter(({ set }) => set === props.expansionCode)
    return filtered
  }, [props.cardData, activeGame.idGame, props.expansionCode])

  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue, setDebouncedInputValue] = useState('')
  const [inputEnterPauses, setInputEnterPauses] = useState<number[]>([])
  const activeStockType = useStockStore((state) => state.activeStockType)

  const debounceSpeed = getDebounceSpeed(inputEnterPauses)

  useEffect(
    () => {
      // Update debounced value after delay
      let handler: any = null
      if (inputValue.length > 2) {
        handler = setTimeout(() => {
          setDebouncedInputValue(inputValue)
        }, 400)
      } else {
        setDebouncedInputValue(inputValue)
      }
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        if (handler) clearTimeout(handler)
      }
    },
    [inputValue, debounceSpeed] // Only re-call effect if value or delay changes
  )

  useEffect(
    () => {
      const start = Date.now()
      // Update debounced value after delay

      return () => {
        if (inputValue.length)
          setInputEnterPauses((inputEnterPauses) => [
            Math.min(Date.now() - start, 2000),
            ...inputEnterPauses.slice(0, 9),
          ])
      }
    },
    [inputValue] // Only re-call effect if value or delay changes
  )

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(false)
  }, [debouncedInputValue])

  const getFilteredOptions = (options: LocalizedCard[]) => {
    if (activeStockType === 'singles') {
      return options.filter((card) => isSingle(card))
    } else if (activeStockType === 'sealed') {
      return options.filter((card) => !isSingle(card))
    }
    return options
  }

  const memoizedOptions = useMemo(() => {
    if (debouncedInputValue.length < 3) return []
    const filteredOptions = getFilteredOptions(options)

    return filterOptions(filteredOptions, debouncedInputValue, props.activeLanguage)
  }, [debouncedInputValue, options, activeStockType, props.activeLanguage])

  useEffect(() => {
    setInputValue('')
  }, [activeStockType])

  // State indicating which cell/index is active in the grid
  const [activeCellIndex, setActiveCellIndex] = useState(-1)
  const [activeCellValue, setActiveCellValue] = useState<CardDataItem | null>(null)
  const [listboxOpen, setListboxOpen] = useState(false)

  // ref for input
  const inputRef = useRef<HTMLInputElement>(null)

  const [windowWidth, setWindowWidth] = useState(window.innerWidth > 700 ? window.innerWidth : 700)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)

  //update windows size when change, so we have responsive design
  useEffect(() => {
    function updateWindowDimensions() {
      if (window.innerWidth > 700) setWindowWidth(window.innerWidth)
      else setWindowWidth(700)
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', updateWindowDimensions)
    return () => window.removeEventListener('resize', updateWindowDimensions)
  }, [])

  const columnWidth = 260 // includes margins
  const columns = Math.max(1, Math.floor(windowWidth / columnWidth)) - 1

  const moveFocus = (direction: string, prev: number) => {
    let newIndex = prev
    switch (direction) {
      case 'ArrowDown': {
        const nextIndex = prev + columns
        if (prev === -1) newIndex = 0
        else if (nextIndex < memoizedOptions.length) newIndex = nextIndex
        break
      }
      case 'ArrowUp': {
        const prevIndex = prev - columns
        if (prevIndex >= 0) newIndex = prevIndex
        break
      }
      case 'ArrowRight': {
        const isLastColumn = prev % columns === columns - 1
        const canGoNext = prev + 1 < memoizedOptions.length
        if (!isLastColumn && canGoNext) {
          newIndex = prev + 1
        } else {
          const nextIndex = prev + 1
          if (nextIndex < memoizedOptions.length) {
            newIndex = nextIndex
          }
        }
        break
      }
      case 'ArrowLeft': {
        const isFirstColumn = prev % columns === 0
        if (!isFirstColumn) {
          newIndex = prev - 1
        } else {
          const prevIndex = prev - 1
          if (prevIndex >= 0) newIndex = prevIndex
        }
        break
      }
    }

    if (memoizedOptions[newIndex]) {
      setActiveCellIndex(newIndex)
      setActiveCellValue(memoizedOptions[newIndex])
    }

    return newIndex
  }

  // Input key management
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault()
      setActiveCellIndex((prev) => moveFocus(e.key, prev))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeCellValue) {
        props.handleCardChange(activeCellValue)
        setInputValue('')
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setListboxOpen(false)
    }
  }

  useEffect(() => {
    if (memoizedOptions.length === 0) {
      setActiveCellIndex(-1)
      setActiveCellValue(null)
    } else if (activeCellValue) {
      const newIndex = memoizedOptions.findIndex((item) => item._id === activeCellValue._id)

      if (newIndex !== -1) {
        setActiveCellIndex(newIndex)
      } else {
        setActiveCellIndex(0)
        setActiveCellValue(memoizedOptions[0])
      }
    } else {
      setActiveCellIndex(0)
      setActiveCellValue(memoizedOptions[0])
    }
  }, [memoizedOptions])

  useEffect(() => {
    if (props.setShowOverlay) props.setShowOverlay(listboxOpen)
  }, [listboxOpen])

  const setActiveValue = (index: number) => {
    setActiveCellIndex(index)
    setActiveCellValue(memoizedOptions[index])
  }

  return (
    <>
      <div className="autocomplete">
        <Autocomplete
          disableListWrap
          inputValue={inputValue}
          filterOptions={(x) => x}
          onInputChange={(e) => {
            //@ts-ignore
            const newValue = e?.target?.value
            if (typeof newValue === 'string') {
              setInputValue(newValue)
              newValue.length > 2 && !memoizedOptions.length && setLoading(true)
            }
          }}
          className="custom-autocomplete"
          data-testid="autocomplete-input"
          freeSolo={true}
          autoHighlight={false}
          autoSelect={false}
          getOptionLabel={cardLabel}
          id="card-autocomplete"
          noOptionsText={t('addArticles.noCardsFound')}
          onChange={(_event) => {
            // instead of selecting the onChange value, I want to select the activeCell manually selected by user using mouse or keyboard
            props.handleCardChange(activeCellValue!)
            setInputValue('')
          }}
          open={listboxOpen}
          onOpen={() => {
            setListboxOpen(true)
          }}
          onClose={() => {
            setListboxOpen(false)
            setActiveCellIndex(-1)
            setActiveCellValue(null)
          }}
          ref={props.autocompleteRef}
          value=""
          ListboxComponent={ListboxComponent as any}
          ListboxProps={
            {
              inputRef,
              onKeyboardSelect: (selectedIndex: number) => {
                if (selectedIndex != null) {
                  props.handleCardChange(memoizedOptions[selectedIndex])
                  setInputValue('')
                }
              },
              setListboxOpen,
              windowHeight,
              windowWidth,
              columns,
              columnWidth,
              activeCell: activeCellIndex,
              setActiveCell: setActiveCellIndex,
              setActiveValue,
            } as any
          }
          options={loading ? [] : memoizedOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              onKeyDown={handleInputKeyDown} // we handle arrows here
              variant="outlined"
              label={activeStockType === 'singles' ? t('card.cardName') : t('card.itemName')}
              placeholder={
                activeStockType === 'singles'
                  ? t('addArticles.startTypingCard')
                  : t('addArticles.startTypingItem')
              }
              fullWidth
            />
          )}
          // Rendering of each option
          renderOption={(option) => {
            const index = memoizedOptions.indexOf(option)
            return (
              <div className="result-item" data-option-index={index}>
                <Typography variant="body2" data-testid={'visual-article-header-' + index}>
                  <b>
                    {props.useKeyruneExpansionIcons && props.rarities ? (
                      <span>
                        {cardLabel(option, props.rarities)}{' '}
                        <i
                          className={`ss ss-2x ss-${normalizeSetCodeForKeyrune(
                            option.set
                          )} ss-${props.rarities[option.rarity].name.toLowerCase()}`}
                        ></i>
                      </span>
                    ) : (
                      cardLabel(option, props.rarities)
                    )}
                  </b>
                </Typography>
                <img alt={option.name || ''} width="180px" src={getProductImageUrl(option._id)} />
              </div>
            )
          }}
        />
      </div>

      {loading && (
        <Box position="relative" bottom="0">
          <Box
            padding={4}
            display="flex"
            style={{
              overflowY: 'hidden',
              position: 'absolute',
              backgroundColor: 'white',
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
              left: `-16px`,
              marginTop: '4px',
              zIndex: 1500,
            }}
            height="350px"
            maxWidth="850px"
            width="90vw"
          >
            {[1, 2, 3, 4].map((index) => {
              return (
                <Box key={index} width="180px" height="400px" marginRight={3} paddingTop={2}>
                  <Skeleton variant="text" animation="wave" height={20} />
                  <Skeleton variant="rect" width={180} height={254} animation="wave" />
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
    </>
  )
}
